import React, {useEffect, useState} from 'react';
import {TextInput, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {
  PRACTICE_DIFFICULTIES,
  DEFAULT_PRACTICE_DIFFICULTY_KEY,
} from '../../constants/practiceDifficulty';

/**
 * The Sandbox entry point, in the Tools tab.
 *
 * "Play vs Computer" used to be something a coach baked into a chapter, which
 * meant the only way to spar a position was to convert the whole chapter to
 * practice mode — and the only way for the coach to try their own exercise was
 * the preview-as-participant toggle. This card makes it a tool instead: anyone,
 * any position, no consequences. Nothing it starts is ever assessed or
 * recorded; the authored Exercise (chapter_mode === 'practice') remains the
 * assessed path.
 *
 * Collapsed it's a setup form; once armed the parent swaps it for the normal
 * PracticePanel, so this component only ever renders the "not playing yet"
 * state plus the disabled explanation.
 */

export type PlayVsComputerCardProps = {
  /** The position the game would start from, for the summary line. */
  positionLabel: string;
  /** Side to move in that position — the default choice. */
  sideToMove: 'w' | 'b';
  onStart: (opts: {
    studentColor: 'w' | 'b';
    difficultyKey: string;
    goal: {type: string; value?: number} | null;
  }) => void;
  /**
   * Set when the viewer is part-way through an assessed Exercise. Sparring the
   * same position freely mid-attempt is an invitation to work the answer out
   * and come back, so the card explains itself instead of just vanishing.
   */
  blockedReason?: string | null;
};

// Mirrors the chapter settings panel's PRACTICE_GOALS, including 'mateIn' —
// which was missing here, so the one goal that needs a number was the one goal
// a Sandbox game could not set.
const GOALS: {key: string; label: string}[] = [
  {key: 'none', label: 'Just play'},
  {key: 'mate', label: 'Checkmate'},
  {key: 'mateIn', label: 'Mate in N'},
  {key: 'equalize', label: 'Equalize'},
  {key: 'advantage', label: 'Get winning'},
  {key: 'promotion', label: 'Promote a pawn'},
];

function Chip({
  label,
  selected,
  onPress,
  disabled,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <TouchableOpacity
      disabled={disabled}
      onPress={onPress}
      style={{
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 14,
        borderWidth: 1,
        opacity: disabled ? 0.5 : 1,
        borderColor: selected
          ? CCColors.Workbook.GoldText
          : updateColorWithOpacity(CCColors.Workbook.Brown, 0.2),
        backgroundColor: selected ? CCColors.LightYellow : 'transparent',
      }}>
      <CCText
        fontSize={11.5}
        fontWeight={selected ? 'bold' : 'normal'}
        color={selected ? CCColors.Workbook.GoldText : CCColors.Workbook.Brown}>
        {label}
      </CCText>
    </TouchableOpacity>
  );
}

export default function PlayVsComputerCard({
  positionLabel,
  sideToMove,
  onStart,
  blockedReason,
}: PlayVsComputerCardProps) {
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState<'w' | 'b'>(sideToMove);
  const [difficultyKey, setDifficultyKey] = useState(
    DEFAULT_PRACTICE_DIFFICULTY_KEY,
  );
  const [goalKey, setGoalKey] = useState('none');
  const [mateInValue, setMateInValue] = useState('3');
  const parsedMateIn = parseInt(mateInValue, 10);
  const mateInIsValid = Number.isInteger(parsedMateIn) && parsedMateIn > 0;

  // Default to whoever is actually to move in the position being armed - it's
  // the only choice that lets you play on from here without the engine moving
  // first, and it changes as the viewer walks the tree with the card open.
  useEffect(() => {
    setColor(sideToMove);
  }, [sideToMove]);

  const isBlocked = Boolean(blockedReason);

  return (
    <Card style={{padding: 12}}>
      <TouchableOpacity
        accessibilityRole="button"
        onPress={() => !isBlocked && setOpen(prev => !prev)}
        disabled={isBlocked}
        style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
        <Box
          style={{
            width: 26,
            height: 26,
            borderRadius: 13,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: CCColors.LightYellow,
          }}>
          <Ionicons
            name="hardware-chip-outline"
            size={15}
            color={CCColors.Workbook.GoldText}
          />
        </Box>
        <Box style={{flex: 1}}>
          <CCText
            fontSize={13}
            fontWeight="bold"
            color={CCColors.TextColor.Primary}>
            Play vs Computer
          </CCText>
          <CCText fontSize={11} color={CCColors.Workbook.Grey}>
            {isBlocked
              ? blockedReason
              : `Play out ${positionLabel} against the engine.`}
          </CCText>
        </Box>
        {!isBlocked && (
          <Ionicons
            name={open ? 'chevron-up' : 'chevron-down'}
            size={15}
            color={CCColors.Workbook.Brown}
          />
        )}
      </TouchableOpacity>

      {open && !isBlocked && (
        <Box style={{marginTop: 12, gap: 12}}>
          <Box>
            <CCText
              fontSize={11}
              fontWeight="bold"
              color={CCColors.Workbook.Grey}
              style={{letterSpacing: 0.5, marginBottom: 6}}>
              YOU PLAY
            </CCText>
            <Box style={{flexDirection: 'row', gap: 6}}>
              <Chip
                label="White"
                selected={color === 'w'}
                onPress={() => setColor('w')}
              />
              <Chip
                label="Black"
                selected={color === 'b'}
                onPress={() => setColor('b')}
              />
            </Box>
            {color !== sideToMove && (
              <>
                <Spacer spacing={4} />
                <CCText fontSize={10.5} color={CCColors.Workbook.Grey}>
                  The computer moves first from this position.
                </CCText>
              </>
            )}
          </Box>

          <Box>
            <CCText
              fontSize={11}
              fontWeight="bold"
              color={CCColors.Workbook.Grey}
              style={{letterSpacing: 0.5, marginBottom: 6}}>
              OPPONENT STRENGTH
            </CCText>
            <Box style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
              {PRACTICE_DIFFICULTIES.map(level => (
                <Chip
                  key={level.key}
                  label={level.label}
                  selected={level.key === difficultyKey}
                  onPress={() => setDifficultyKey(level.key)}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <CCText
              fontSize={11}
              fontWeight="bold"
              color={CCColors.Workbook.Grey}
              style={{letterSpacing: 0.5, marginBottom: 6}}>
              GOAL (OPTIONAL)
            </CCText>
            <Box style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
              {GOALS.map(g => (
                <Chip
                  key={g.key}
                  label={g.label}
                  selected={g.key === goalKey}
                  onPress={() => setGoalKey(g.key)}
                />
              ))}
            </Box>
            {goalKey === 'mateIn' && (
              <>
                <Spacer spacing={8} />
                <TextInput
                  value={mateInValue}
                  onChangeText={setMateInValue}
                  placeholder="Number of moves, e.g. 3"
                  placeholderTextColor={CCColors.Workbook.Grey}
                  keyboardType="numeric"
                  style={{
                    height: 32,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: updateColorWithOpacity(
                      CCColors.Workbook.Brown,
                      0.2,
                    ),
                    paddingHorizontal: 10,
                    fontSize: 12,
                    color: CCColors.TextColor.Primary,
                  }}
                />
                {!mateInIsValid && (
                  <>
                    <Spacer spacing={4} />
                    <CCText fontSize={10.5} color={CCColors.Red}>
                      Enter a whole number of moves greater than zero.
                    </CCText>
                  </>
                )}
              </>
            )}
          </Box>

          <TouchableOpacity
            disabled={goalKey === 'mateIn' && !mateInIsValid}
            onPress={() =>
              onStart({
                studentColor: color,
                difficultyKey,
                goal:
                  goalKey === 'none'
                    ? null
                    : goalKey === 'mateIn'
                    ? {type: goalKey, value: parsedMateIn}
                    : {type: goalKey},
              })
            }
            style={{
              backgroundColor: CCColors.Primary.Brown,
              paddingVertical: 11,
              borderRadius: 8,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: goalKey === 'mateIn' && !mateInIsValid ? 0.5 : 1,
            }}>
            <CCText color={CCColors.White} fontWeight="bold" fontSize={12.5}>
              Play from this position
            </CCText>
          </TouchableOpacity>

          <CCText fontSize={10.5} color={CCColors.Workbook.Grey}>
            Practice games aren't saved or graded, and don't change the chapter.
          </CCText>
        </Box>
      )}
    </Card>
  );
}
